import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowUp } from 'lucide-react'
import Logo from '@/components/generals/logo'
import MainContainer from '@/components/generals/main-container'
import EditorialGrid from '@/components/generals/editorial-grid'
import { EditableLink } from '@/components/elements/editable'
import { siteConfig } from '@/constants/site'
import { profile } from '@/constants/profile'

const FooterHeading: FC<{ children: ReactNode }> = ({ children }): ReactNode => (
    <h2 className='font-mono text-label uppercase text-muted-foreground'>
        {children}
    </h2>
)

const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='tone-ink'>
            <MainContainer className='pt-16 pb-10 md:pt-24'>
                <EditorialGrid className='gap-y-12'>
                    <p className='col-span-4 font-heading text-headline [font-weight:380] md:col-span-8'>
                        {siteConfig.philosophy.map((line, index) => (
                            <span key={line} className='block'>
                                {index === 1 ? <em className='text-brand'>{line}</em> : line}
                            </span>
                        ))}
                    </p>

                    <div className='col-span-4 flex flex-col gap-5 md:col-span-4 md:items-start md:justify-end'>
                        <Logo tone='dark' />

                        <p className='max-w-[36ch] text-[0.9375rem] leading-relaxed text-muted-foreground'>
                            An independent technology hub documenting software engineering, AI experiments,
                            autonomous agents and practical automation.
                        </p>
                    </div>
                </EditorialGrid>

                <EditorialGrid className='mt-16 gap-y-10 border-t border-border pt-10 md:mt-24'>
                    <nav aria-labelledby='footer-sections' className='col-span-2 md:col-span-3'>
                        <FooterHeading>
                            <span id='footer-sections'>Sections</span>
                        </FooterHeading>

                        <ul className='mt-5 flex flex-col gap-3'>
                            {siteConfig.nav.map((item) => (
                                <li key={item.href}>
                                    <Link href={item.href} className='link-underline text-[0.9375rem] hover:text-brand'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='col-span-2 md:col-span-3'>
                        <FooterHeading>Elsewhere</FooterHeading>

                        <ul className='mt-5 flex flex-col items-start gap-3 text-[0.9375rem]'>
                            <li>
                                <EditableLink href={profile.links.github} label='GitHub' />
                            </li>
                            <li>
                                <EditableLink href={profile.links.linkedin} label='LinkedIn' />
                            </li>
                            <li>
                                <a href='/rss.xml' className='link-underline hover:text-brand'>
                                    RSS feed
                                </a>
                            </li>
                        </ul>
                    </div>

                    <div className='col-span-4 md:col-span-4 md:col-start-9'>
                        <FooterHeading>Colophon</FooterHeading>

                        <p className='mt-5 max-w-[38ch] text-[0.9375rem] leading-relaxed text-muted-foreground'>
                            Set in Fraunces, IBM Plex Sans and IBM Plex Mono. Built with Next.js, written in
                            Markdown, and kept deliberately light on JavaScript.
                        </p>
                    </div>
                </EditorialGrid>

                <div className='mt-14 flex flex-col-reverse gap-4 border-t border-border pt-6 font-mono text-caption text-muted-foreground sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        <span suppressHydrationWarning>&copy; {new Date().getFullYear()}</span> {siteConfig.name}. {siteConfig.tagline}
                    </p>

                    <a href='#top' className='group inline-flex items-center gap-2 self-start uppercase tracking-label hover:text-foreground sm:self-auto'>
                        Back to top
                        <ArrowUp aria-hidden='true' className='size-3.5 transition-transform duration-300 ease-editorial group-hover:-translate-y-0.5' />
                    </a>
                </div>
            </MainContainer>
        </footer>
    )
}

export default SiteFooter
